/**
 * React NodeView for `image` on the wiki editor surface. Bound by
 * `buildWikiImageExtension` (only registered when `surface === 'wiki'`).
 *
 * Four branches, in priority order:
 *
 *   1. UPLOADING — `node.attrs.uploadingBlobUrl` is a live blob URL
 *      captured at file-pick time by `<WikiEmbedOrchestrator>`. Renders
 *      `<img src={blobUrl}>` dimmed, with a centered spinner overlay
 *      and a corner Cancel button. The upload orchestrator owns the
 *      AbortController; clicking Cancel fires the
 *      `wiki:cancel-image-upload` window event so the orchestrator can
 *      tear down + best-effort `DELETE /api/uploads/:id` the row.
 *
 *   2. DONE / signed-URL — `attachmentId` is set, `uploadingBlobUrl` is
 *      cleared. Resolves the signed GET URL via
 *      `useImagePresignedUrl(attachmentId)`. Renders `<img>` plus a
 *      click-to-open lightbox affordance. On `<img onError>` calls
 *      `refresh()` to mint a fresh URL (mirrors `VideoEmbedView`'s
 *      refresh-on-error path).
 *
 *   3. LEGACY MARKDOWN — `src` is an absolute URL, `attachmentId === null`.
 *      Renders `<img src>` directly. Pre-Phase-1 wiki docs that pasted
 *      a remote image URL via `![alt](https://…)` flow through here.
 *      Still opens the lightbox; the lightbox treats absolute URLs as
 *      single-image carousels.
 *
 *   4. ERROR — `attachmentId` set + signed URL returned 403 / 404, OR
 *      `attachmentId === null` AND `src === null` (orphan state). Renders
 *      a localized "Image unavailable" placeholder mirroring
 *      `VideoEmbedView`'s restricted/deleted chrome.
 *
 * The lightbox is opened by dispatching the `wiki:open-image-lightbox`
 * window event with `{ attachmentId | src }` so the orchestrator can
 * collect every image NodeView on the page and present them as a
 * carousel — see `WikiImageLightbox.tsx` for the listener side.
 */

import type { NodeViewProps } from '@tiptap/react'
import { NodeViewWrapper } from '@tiptap/react'
import { AlertCircle, ImageOff, Loader2, RotateCcw, X } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useImagePresignedUrl } from '@/features/wiki/hooks/use-image-presigned-url'
import { externalImageLinkHref, isAllowedImageSrc } from '@/lib/allowed-image-origins'

import {
  CANCEL_IMAGE_UPLOAD_EVENT,
  type CancelImageUploadDetail,
  DISMISS_EMBED_UPLOAD_EVENT,
  type EmbedUploadActionDetail,
  OPEN_IMAGE_LIGHTBOX_EVENT,
  type OpenImageLightboxDetail,
  RETRY_EMBED_UPLOAD_EVENT,
} from '@/features/wiki/lib/embed-events'
import { ExternalImageLink } from './ExternalImageLink'
import { peekUploadBlob, releaseUploadBlob } from './upload-blob-bridge'

/**
 * NodeView for the wiki `image` node. `NodeViewProps` is the
 * `@tiptap/react` 2.27 callback shape — `node` carries the live attrs.
 */
export function WikiImageNodeView({ node }: NodeViewProps) {
  const { t } = useTranslation('wiki')
  const attachmentId = (node.attrs.attachmentId as string | null) ?? null
  const uploadingFailed = (node.attrs.uploadingFailed as boolean) ?? false
  // A `src` outside our own origins is never loaded as an image: it is drawn
  // as a link in the legacy branch below.
  const rawSrc = (node.attrs.src as string | null) ?? null
  const srcAttr = isAllowedImageSrc(rawSrc) ? rawSrc : null
  const externalHref = !attachmentId && !srcAttr ? externalImageLinkHref(rawSrc) : null
  const altAttr = (node.attrs.alt as string | null) ?? ''
  const uploadingBlobUrl = (node.attrs.uploadingBlobUrl as string | null) ?? null

  // Signed-URL hook — gated on `attachmentId` to avoid pinging the BE
  // for legacy-markdown / placeholder states. `enabled` inside the
  // hook handles the empty case.
  const { url: signedUrl, isError, status, refresh } = useImagePresignedUrl(attachmentId)

  // Bridge from the just-finished upload: the bytes are already in
  // memory, so keep painting them until the signed image has actually
  // decoded. Read once per attachment — `peekUploadBlob` is only
  // non-null in the seconds after this node's own upload resolved.
  const [bridgedBlobUrl, setBridgedBlobUrl] = useState<string | null>(() =>
    peekUploadBlob(attachmentId),
  )
  const [signedImageReady, setSignedImageReady] = useState(false)

  useEffect(() => {
    setBridgedBlobUrl(peekUploadBlob(attachmentId))
    setSignedImageReady(false)
  }, [attachmentId])

  // The real image decoded — drop the bridge and let the blob's bytes go.
  const handleSignedImageLoad = useCallback(() => {
    setSignedImageReady(true)
    if (bridgedBlobUrl) {
      releaseUploadBlob(attachmentId)
      setBridgedBlobUrl(null)
    }
  }, [attachmentId, bridgedBlobUrl])

  // --- 4. ERROR / unavailable: attachment row is missing OR the signed
  // URL came back 403 / 404. We render the localized fallback below.
  const isUnavailable = attachmentId !== null && (isError || status === 403 || status === 404)

  // Click → open lightbox. The orchestrator (Doc-page-level) listens
  // and walks the editor's doc tree to compose the carousel array,
  // which is why we don't pass any neighbor context here — the
  // lightbox owns its own indexing.
  const handleClick = useCallback(() => {
    if (typeof window === 'undefined') return
    if (uploadingBlobUrl) return // still uploading — click is a no-op
    if (isUnavailable) return // error state — click is a no-op
    const detail: OpenImageLightboxDetail = {
      attachmentId,
      src: srcAttr,
      alt: altAttr,
    }
    window.dispatchEvent(
      new CustomEvent<OpenImageLightboxDetail>(OPEN_IMAGE_LIGHTBOX_EVENT, { detail }),
    )
  }, [attachmentId, srcAttr, altAttr, uploadingBlobUrl, isUnavailable])

  // <img onError> — when the signed URL has expired between mint and
  // browser load (~1h TTL with a 30-min refresh cadence, so this is
  // rare but possible across multi-hour open sessions), refresh and
  // let TanStack Query's swap to the new URL re-render the <img>.
  // Mirrors VideoEmbedView's MEDIA_ERR_NETWORK recovery.
  const handleImgError = useCallback(() => {
    if (attachmentId) {
      void refresh()
    }
  }, [attachmentId, refresh])

  // Cancel button on the uploading branch — orchestrator owns the
  // AbortController + row cleanup. We just dispatch the event with the
  // blob URL so the orchestrator can match the in-flight upload (it
  // keyed its state on the blob URL).
  const handleCancelUpload = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      if (typeof window === 'undefined' || !uploadingBlobUrl) return
      const detail: CancelImageUploadDetail = { uploadingBlobUrl }
      window.dispatchEvent(
        new CustomEvent<CancelImageUploadDetail>(CANCEL_IMAGE_UPLOAD_EVENT, { detail }),
      )
    },
    [uploadingBlobUrl],
  )

  // --- 1. UPLOADING branch.
  // UPLOAD FAILED — keep the dimmed preview visible with Retry /
  // Dismiss instead of silently removing it (a vanished skeleton reads
  // as "the drop did nothing"). The orchestrator kept the original File
  // keyed on the blob URL, so Retry re-runs the network leg against
  // this same node; the blob URL stays alive until dismiss / resolve.
  // Never persists — the flush-time strip removes the node.
  if (uploadingBlobUrl && uploadingFailed) {
    const dispatchAction = (eventName: string) => (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      if (typeof window === 'undefined') return
      window.dispatchEvent(
        new CustomEvent<EmbedUploadActionDetail>(eventName, {
          detail: { key: uploadingBlobUrl },
        }),
      )
    }
    return (
      <NodeViewWrapper
        as="span"
        data-block-type="wiki-image"
        data-state="error"
        className="relative my-1 inline-block max-w-full align-baseline"
        contentEditable={false}
      >
        {/* Controls sit in a strip BELOW the image, not overlaid — a
            small dropped image can't contain an overlay, and the badge +
            buttons would spill past its bounds. */}
        <img
          src={uploadingBlobUrl}
          alt={altAttr || t('docDrop.failedLabel')}
          className="h-auto max-w-full rounded-md opacity-40"
          draggable={false}
        />
        <div className="mt-1.5 flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-danger">
            <AlertCircle size={14} aria-hidden="true" />
            {t('docDrop.failedLabel')}
          </span>
          <button
            type="button"
            onClick={dispatchAction(RETRY_EMBED_UPLOAD_EVENT)}
            onMouseDown={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1 rounded-md border border-border bg-bg-elevated px-2 py-0.5 text-xs font-medium text-fg hover:bg-bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <RotateCcw aria-hidden="true" size={12} />
            {t('docDrop.retry')}
          </button>
          <button
            type="button"
            onClick={dispatchAction(DISMISS_EMBED_UPLOAD_EVENT)}
            onMouseDown={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium text-fg-muted hover:bg-bg-muted hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            {t('docDrop.dismiss')}
          </button>
        </div>
      </NodeViewWrapper>
    )
  }

  if (uploadingBlobUrl) {
    return (
      <NodeViewWrapper
        as="span"
        data-block-type="wiki-image"
        data-state="uploading"
        className="relative my-1 inline-block max-w-full align-baseline"
        contentEditable={false}
      >
        <img
          src={uploadingBlobUrl}
          alt={altAttr || t('attachment.uploading')}
          className="h-auto max-w-full rounded-md opacity-50"
          draggable={false}
        />
        <output className="absolute inset-0 flex items-center justify-center" aria-live="polite">
          <Loader2
            size={32}
            aria-hidden="true"
            className="animate-spin text-white drop-shadow-md"
          />
          <span className="sr-only">{t('attachment.uploading')}</span>
        </output>
        <button
          type="button"
          onClick={handleCancelUpload}
          onMouseDown={(e) => e.stopPropagation()}
          aria-label={t('attachment.cancel')}
          title={t('attachment.cancel')}
          className="absolute end-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-full bg-black/55 text-white hover:bg-black/75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          <X size={14} aria-hidden="true" />
        </button>
      </NodeViewWrapper>
    )
  }

  // --- 4. ERROR / unavailable branch.
  if (isUnavailable) {
    return (
      <NodeViewWrapper
        as="span"
        data-block-type="wiki-image"
        data-state="unavailable"
        className="my-1 inline-flex items-center gap-2 rounded-md border border-border bg-bg-muted px-3 py-2 align-baseline text-sm text-fg-subtle"
        contentEditable={false}
      >
        <ImageOff size={16} aria-hidden="true" />
        <span>{t('image.unavailable')}</span>
      </NodeViewWrapper>
    )
  }

  // --- 3b. EXTERNAL branch — legacy absolute URL outside our own origins.
  if (externalHref) {
    return (
      <NodeViewWrapper
        as="span"
        data-block-type="wiki-image"
        data-state="external"
        className="my-1 inline-block max-w-full align-baseline"
        contentEditable={false}
      >
        <ExternalImageLink href={externalHref} alt={altAttr} />
      </NodeViewWrapper>
    )
  }

  // --- 3. LEGACY MARKDOWN branch — absolute URL, no attachmentId.
  if (!attachmentId && srcAttr) {
    return (
      <NodeViewWrapper
        as="span"
        data-block-type="wiki-image"
        data-state="legacy"
        className="my-1 inline-block max-w-full align-baseline"
        contentEditable={false}
      >
        <button
          type="button"
          onClick={handleClick}
          onMouseDown={(e) => e.stopPropagation()}
          aria-label={altAttr || t('image.unavailable')}
          className="block max-w-full cursor-zoom-in rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          <img
            src={srcAttr}
            alt={altAttr}
            loading="lazy"
            onError={handleImgError}
            className="h-auto max-w-full rounded-md"
            draggable={false}
          />
        </button>
      </NodeViewWrapper>
    )
  }

  // --- 2. DONE branch — attachmentId set, signed URL resolved.
  // While the signed URL is fetching for the first time, render a soft
  // loading skeleton so the doc doesn't flash a broken icon.
  // Only when there is genuinely nothing to paint. With a bridged blob
  // in hand the skeleton would be a REGRESSION — it replaces the image
  // the user just watched upload with an empty box, and collapses the
  // layout while it does.
  if (attachmentId && !signedUrl && !bridgedBlobUrl) {
    return (
      <NodeViewWrapper
        as="span"
        data-block-type="wiki-image"
        data-state="loading"
        className="my-1 inline-flex items-center justify-center rounded-md border border-border bg-bg-muted px-4 py-6 align-baseline text-sm text-fg-subtle"
        contentEditable={false}
      >
        <Loader2 size={18} aria-hidden="true" className="me-2 animate-spin" />
        <span className="sr-only">{t('attachment.uploading')}</span>
      </NodeViewWrapper>
    )
  }

  // The happy path — attachmentId + signed URL both present.
  return (
    <NodeViewWrapper
      as="span"
      data-block-type="wiki-image"
      data-attachment-id={attachmentId ?? undefined}
      data-state="ready"
      className="my-1 inline-block max-w-full align-baseline"
      contentEditable={false}
    >
      <button
        type="button"
        onClick={handleClick}
        onMouseDown={(e) => e.stopPropagation()}
        aria-label={altAttr || t('image.unavailable')}
        className="block max-w-full cursor-zoom-in rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
      >
        {/*
         * While the bridge is live the blob is the ONLY thing in flow,
         * at its natural size, and the signed image is fetched by an
         * out-of-flow preloader purely to learn when it has decoded.
         *
         * The blob must never be sized against the real image. An <img>
         * that has not loaded measures 0px, so an overlay parent sized
         * by it collapses to zero height and an `inset-0 h-full` blob
         * renders at 0px — invisible for exactly the duration of the S3
         * fetch, which is the gap this whole bridge exists to remove.
         * (Measured: parent 0px, blob 0px. That shipped once already.)
         *
         * `hidden` is display:none, which still fetches and still fires
         * `onLoad` — and `loading="lazy"` is deliberately omitted there,
         * since a deferred fetch would strand the bridge until the node
         * scrolled into view.
         */}
        {bridgedBlobUrl && !signedImageReady ? (
          <>
            <img
              src={bridgedBlobUrl}
              alt={altAttr}
              className="h-auto max-w-full rounded-md"
              draggable={false}
            />
            {signedUrl ? (
              <img
                src={signedUrl}
                alt=""
                aria-hidden="true"
                className="hidden"
                onLoad={handleSignedImageLoad}
                onError={handleImgError}
              />
            ) : null}
          </>
        ) : (
          <img
            src={signedUrl}
            alt={altAttr}
            loading="lazy"
            onError={handleImgError}
            onLoad={handleSignedImageLoad}
            className="h-auto max-w-full rounded-md"
            draggable={false}
          />
        )}
      </button>
    </NodeViewWrapper>
  )
}

WikiImageNodeView.displayName = 'WikiImageNodeView'
